import { RequestData } from '@ant-design/pro-table';
import { request } from '@/utils/request';
import { Application } from './card-application';
import { ClientFootprint } from './user';

export interface SolutionApplicationItem {
  id: string;
  solutionId: string;
  userId: string;
  user: {
    name: string;
    email: string;
    accountId: string;
    id: string;
    relationshipManager?: string;
    vendorName: string;
  };
  rm: string;
  solutionName: string;
  status: string;
  lastUpdated: string;
  updatedAt?: Date;
  startedAt?: Date;
  operatorName?: string;
  clientFootprint: ClientFootprint;
  name?: string;
  solutionType?: string;
  solutionCode?: string;
  cardApplication?: Application;
  cardProduct?: SolutionCardProduct | null;
  sumsubReferenceInfo?: SolutionSumsubReferenceInfo;
  onboardingMode?: 'SUMSUB' | 'LEGACY';
}

export interface DefaultClientDocument {
  status: string;
  fileName: string;
  extension: string;
  documentKey: string;
  documentUrl: string;
}

export type DefaultClientDocumentResponse = Record<string, DefaultClientDocument[]>;

export enum SolutionType {
  UTGL_ACCOUNT_UPGRADE = 'UTGL_ACCOUNT_UPGRADE',
  UTGL_VIRTUAL_CARD_APPLICATION = 'UTGL_VIRTUAL_CARD_APPLICATION',
  UTGL_ONBOARDING = 'UTGL_ONBOARDING',
  UZPAY_ONBOARDING = 'UZPAY_ONBOARDING',
  FXP_ONBORADING = 'FXP_ONBORADING',
  FGA_LEGACY = 'FGA_LEGACY',
  LIVERUM_ACCOUNT_UPGRADE = 'LIVERUM_ACCOUNT_UPGRADE',
  UTGL_COBRAND = 'UTGL_COBRAND',
  UTGL_OPERATIONS = 'UTGL_OPERATIONS',
  LIVERUM_OPERATIONS = 'LIVERUM_OPERATIONS'
}

const OPERATIONS_SOLUTION_TYPES: ReadonlySet<string> = new Set([
  SolutionType.UTGL_OPERATIONS,
  SolutionType.LIVERUM_OPERATIONS
]);

export function isOperationsSolutionType(solutionType?: string | null): boolean {
  return solutionType != null && OPERATIONS_SOLUTION_TYPES.has(solutionType);
}

export function isInternalSolutionType(solutionType?: string | null): boolean {
  return solutionType === SolutionType.UTGL_COBRAND || isOperationsSolutionType(solutionType);
}

/**
 * KYC Required Fields configuration from solution initial state
 * Defines which fields are required for identity verification
 */
export interface KycRequiredFields {
  personalInformation?: {
    firstName?: { type: string };
    surname?: { type: string };
    residentialAddress?: { type: string };
    documentIssueDate?: { type: string };
  };
  idDocument?: {
    oneOf?: Array<
      | { idFrontUrl?: { type: string }; idBackUrl?: { type: string } }
      | { idPassportUrl?: { type: string } }
    >;
  };
  facial?: {
    idSelfieUrl?: { type: string };
  };
  signature?: {
    signatureUrl?: { type: string };
  };
  employment?: {
    employmentStatus?: { type: string };
    jobTitle?: { type: string };
    companyName?: { type: string };
    businessNature?: { type: string };
    annualIncome?: { type: string };
    employmentLocation?: { type: string };
  };
  addressProof?: {
    addressProofUrl?: { type: string };
  };
  sourceOfWealth?: {
    sources?: { type: string };
    wealthProofUrl?: { type: string };
  };
}

export interface SolutionSumsubKycDisplayConfig {
  includeInSolutionApplication?: boolean;
  includeInUserProfile?: boolean;
  fieldAllowlist?: string[];
}

export interface SolutionSumsubKycProfileConfig {
  applicantType?: 'individual' | 'company';
  configurationRevision?: string;
  corporateRequirements?: {
    validated: boolean; environment: 'sandbox' | 'production'; liveness: boolean; emailVerification: boolean;
    associatedPartyChecksVerified: boolean; requiredProfileFields: string[];
    associatedPartyLevels?: { individual?: string[]; company?: string[] };
    handoffValidated?: boolean; remediableRejectLabels?: string[];
  };
  initiationSource: 'CLIENT_APP' | 'OPS_ADMIN';
  onboardingSurface?: 'CLIENT_APP' | 'OPS_ADMIN';
  submissionMode: 'SDK' | 'API';
  requiredForSolutionApproval?: boolean;
  levelName: string;
  syncTarget: 'CLIENT_IDENTITY' | 'SOLUTION_APPLICATION';
  mappingProfile?: string;
  display?: SolutionSumsubKycDisplayConfig;
}

export interface SolutionSumsubKycConfig {
  enabled?: boolean;
  /** Explicit opt-in for new corporate verification; existing attempts remain available. */
  corporateEnabled?: boolean;
  profiles?: Record<string, SolutionSumsubKycProfileConfig>;
}

export type SolutionApplicationFormVersion = 1 | 2 | '1' | '2';

/**
 * Solution Initial State - configuration flags that drive application behavior
 * These flags are used to control UI behavior without hardcoding solution codes
 */
export interface SolutionInitialState {
  /** Skip card application flow entirely */
  _skipCardApplication?: boolean;
  /** Max beneficiaries (0 = skip beneficiary management, null = unlimited, number = max count) */
  _maxNumberOfBeneficiaries?: number | null;
  /** KYC required fields configuration */
  _kycRequiredFields?: KycRequiredFields;
  /** Primary card product ID */
  _cardProductId?: number;
  /** Alternative card product IDs resolved by user's existing credit line currency */
  _cardProductWhitelist?: number[];
  /** Virtual card product ID (for solutions that issue virtual card alongside physical) */
  _virtualCardProductId?: number;
  /** Issue virtual card on approval */
  _issueVirtualCard?: boolean;
  /** Skip only virtual card issuance while keeping main card application */
  skipVirtualCardApplication?: boolean;
  /** Power of Attorney required */
  _poa_required?: boolean;
  /** Skip application fee entirely */
  _skipAccountApplicationFeeCharge?: boolean;
  /** Card account types for cobrand solutions (multiple) */
  _cardAccountTypes?: string[];
  /** Enable operation account for an operations solution */
  _operationAccountEnabled?: boolean;
  /** Temporary solution application form version selector. v1 = legacy application form, v2 = Sumsub KYC form. */
  _solutionApplicationFormVersion?: SolutionApplicationFormVersion;
  /** Solution-scoped Sumsub KYC configuration */
  _sumsubKycConfig?: SolutionSumsubKycConfig;
  /** Operator dual-onboarding mode selector capability */
  _operatorOnboardingModeConfig?: {
    enabled?: boolean;
    selectionRequired?: boolean;
    allowedModes?: Array<'SUMSUB' | 'LEGACY'>;
    fallbackMode?: 'SUMSUB' | 'LEGACY';
  };
}

/**
 * Card Product metadata for solution
 */
export interface SolutionCardProduct {
  id: number;
  name: string;
  displayName: string;
  /** Card type: VIRTUAL or PHYSICAL */
  cardType: 'VIRTUAL' | 'PHYSICAL';
  imageUrl?: string | null;
}

/**
 * Solution list item with metadata for configuration-driven UI behavior
 */
export interface SolutionItem {
  id: string;
  code: string;
  name: string;
  type: SolutionType;
  available: boolean;
  /** Present when solutions are blocked due to sanctions/compliance restrictions */
  restrictionReason?: 'SANCTIONED_NATIONALITY';
  /** Solution configuration flags from initial state */
  initialState?: SolutionInitialState;
  /** Card product metadata (if solution has card product configured) */
  cardProduct?: SolutionCardProduct | null;
}


export async function querySolutionApplications(params: any): Promise<RequestData<SolutionApplicationItem>> {
  if (params.user && params.user.id) {
    params.userId = params.user.id;
  }

  return request<RequestData<SolutionApplicationItem>>('/solutions', { params });
}

export async function querySolutionList(params: any): Promise<RequestData<SolutionItem>> {
  if (params.user && params.user.id) {
    params.userId = params.user.id;
  }

  return request<RequestData<SolutionItem>>('/solutions/list', { params: { current: 1, pageSize: 10, ...params } });
}

export interface RestrictedNationalityOverrideRequest {
  managementApprovalObtained: true;
  approvedBy: string;
  approvalDate: string;
  approvalReference: string;
  reason: string;
}

export async function createSolutionApplication(params: {
  userId: string;
  solutionId: string;
  onboardingMode?: 'SUMSUB' | 'LEGACY';
  restrictedNationalityOverride?: RestrictedNationalityOverrideRequest;
  _yotpToken?: string;
}): Promise<any> {
  return request<{ id: string }>(params.restrictedNationalityOverride ? '/solution/application/restricted-nationality-override' : '/solution/application', {
    method: 'POST',
    data: params,
    getResponse: true
  });
}

export interface PreviewReusableSolutionSumsubKycResponse {
  canReuse: boolean;
  sourceApplicationId?: string;
  sourceSolutionId?: string;
  sourceSolutionCode?: string;
  sourceSolutionName?: string;
  status?: string;
  applicantId?: string;
  syncTarget?: string;
}

export const previewReusableSolutionSumsubKyc = (userId: string, solutionId: string) =>
  request<PreviewReusableSolutionSumsubKycResponse>(`/${userId}/solution/${solutionId}/sumsub/reusable-kyc-preview`, {
    method: 'GET'
  });

export type PreviewSumsubApplicantImportResponse =
  | {
      syncStatus: 'SYNCING';
      applicantId: string;
      message: string;
    }
  | {
      syncStatus: 'READY';
      applicantId: string;
      externalUserId: string;
      levelName: string;
      configuredLevelName?: string;
      importedApplicantLevelName?: string;
      levelNameMismatch?: boolean;
      reviewStatus: string;
      reviewAnswer: 'GREEN' | 'RED' | null;
      applicantDisplayName?: string;
      initialValues: SolutionSumsubAdminApiFormValues;
      derivedKycStatus: SolutionSumsubKycStatus;
    };

export interface ImportSumsubApplicantIntoSolutionApplicationRequest {
  applicantId: string;
  applicationId?: string;
  remark?: string;
  remarkChannel?: string;
  _yotpToken: string;
}

export interface ImportSumsubApplicantIntoSolutionApplicationResponse {
  applicationId: string;
  createdApplication: boolean;
  workflowStarted: boolean;
  workflowId?: string;
  status: SolutionSumsubKycStatus;
  levelName: string;
  configuredLevelName?: string;
  importedApplicantLevelName?: string;
  applicantId: string;
  reviewStatus: string;
  reviewAnswer: 'GREEN' | 'RED' | null;
  levelNameMismatch?: boolean;
  initialValues: SolutionSumsubAdminApiFormValues;
}

export const previewSumsubApplicantImport = (
  userId: string,
  solutionId: string,
  applicantId: string,
  applicationId?: string
) =>
  request<PreviewSumsubApplicantImportResponse>(`/${userId}/solution/${solutionId}/sumsub/applicant-preview`, {
    method: 'GET',
    params: {
      applicantId,
      ...(applicationId ? { applicationId } : {})
    }
  });

export const importSumsubApplicantIntoSolutionApplication = (
  userId: string,
  solutionId: string,
  body: ImportSumsubApplicantIntoSolutionApplicationRequest
) =>
  request<ImportSumsubApplicantIntoSolutionApplicationResponse>(`/${userId}/solution/${solutionId}/sumsub/import-applicant`, {
    method: 'POST',
    data: body
  });

export async function getSolutionApplicationStateById({
  solutionApplicationId,
  userId
}: {
  solutionApplicationId: string;
  userId: string;
}) {
  return request<any>(`/${userId}/solution/application/${solutionApplicationId}/state`, {
    method: 'GET',
    getResponse: true
  });
}

export const revenuePreview = (data: any) =>
  request<any>(`/revenue/preview`, {
    method: 'POST',
    data: data,
    getResponse: true
  });

export const updateSolution = (data: any) =>
  request<any>(`/${data.userId}/solution/application/${data.id}/edit`, {
    method: 'POST',
    data: data,
    getResponse: true
  });

export const changeStatusSolution = (data: any) =>
  request<any>(`/solution/change-status`, {
    method: 'POST',
    data: data,
    getResponse: true
  });

export const approveSolution = (data: any) =>
  request<any>(`/${data.userId}/solution/application/${data.id}/approve`, {
    method: 'POST',
    data: data,
    getResponse: true
  });

export const getSolutionApplicationDefaultClientDocuments = (userId: string) => {
  return request<DefaultClientDocumentResponse>(`/client-document/document-types/default/${userId}`, {
    method: 'GET'
  });
};

export interface SolutionApplicationStats {
  pendingApproval: number;
  inProgress: number;
  completed: number;
  rejected: number;
  inactive: number;
}

export async function getSolutionApplicationStats(): Promise<SolutionApplicationStats> {
  return request<SolutionApplicationStats>('/solutions/stats', {
    method: 'GET'
  });
}

export type SolutionSumsubKycStatus =
  | 'NOT_STARTED'
  | 'SDK_STARTED'
  | 'SUBMITTED'
  | 'PENDING_REVIEW'
  | 'ACTION_REQUIRED'
  | 'APPROVED'
  | 'REJECTED'
  | 'FAILED';

export interface SolutionSumsubReferenceInfo {
  applicantType?: 'individual' | 'company';
  configurationRevision?: string;
  verificationRevision?: number;
  onboardingSurface?: 'CLIENT_APP' | 'OPS_ADMIN';
  corporateReady?: boolean;
  applicantRef?: string | null;
  externalUserId?: string | null;
  levelName: string;
  submissionMode: 'SDK' | 'API';
  initiationSource?: 'CLIENT_APP' | 'OPS_ADMIN';
  syncTarget?: 'CLIENT_IDENTITY' | 'SOLUTION_APPLICATION';
  status: SolutionSumsubKycStatus;
  reviewStatus?: string | null;
  reviewAnswer?: 'GREEN' | 'RED' | null;
  actionRequiredReason?: string | null;
  rejectionSummary?: string | null;
  rejectionLabels?: string[];
  reviewRejectType?: string | null;
  moderationComment?: string | null;
  clientComment?: string | null;
  startedAt?: string | null;
  submittedAt?: string | null;
  approvedAt?: string | null;
  rejectedAt?: string | null;
  updatedAt?: string | null;
  profileKey?: string;
  requiredForSolutionApproval?: boolean;
  mappingProfile?: string;
}

export interface SolutionSumsubAdminApiFormField {
  field: string;
  label?: string;
  description?: string;
  placeholder?: string;
  type: string;
  required: boolean;
  sourceField?: string;
  values?: string[];
  format?: string;
  condition?: string;
  displayOrder?: number;
  group?: string;
  sumsubRequired?: boolean;
  idDocType?: string;
}

export interface SolutionSumsubAdminApiFormDocumentSet {
  idDocSetType: string;
  allowedTypes: string[];
  oneOf?: string[];
  required: boolean;
  rules?: Array<{
    condition?: string;
    requirements?: {
      requiredSides?: Array<'FRONT_SIDE' | 'BACK_SIDE'>;
    };
  }>;
  doubleSidedTypes?: string[];
  videoRequired?: string;
  captureMode?: string;
}

export interface SolutionSumsubAdminApiFormSection {
  sectionId: string;
  title?: string | null;
  description?: string | null;
  condition?: string | null;
  displayOrder?: number;
  fields: SolutionSumsubAdminApiFormField[];
}

export interface SolutionSumsubAdminApiFormGroup {
  groupKey: 'identityDocument' | 'addressInformation' | 'personalInformation' | 'liveness' | 'questionnaire';
  title: string;
  description?: string;
  required?: boolean;
  readonly?: boolean;
  skipped?: boolean;
  fields?: SolutionSumsubAdminApiFormField[];
  documentSets?: SolutionSumsubAdminApiFormDocumentSet[];
  questionnaireSections?: SolutionSumsubAdminApiFormSection[];
}

export interface SolutionSumsubAdminApiFormSchema {
  applicationId?: string;
  levelName: string;
  questionnaireId: string | null;
  applicantFields: SolutionSumsubAdminApiFormField[];
  documentSets: SolutionSumsubAdminApiFormDocumentSet[];
  questionnaireSections: SolutionSumsubAdminApiFormSection[];
  groups?: SolutionSumsubAdminApiFormGroup[];
}

export interface SolutionSumsubAdminApiDocumentInput {
  metadata: {
    idDocType: string;
    idDocSubType?: 'FRONT_SIDE' | 'BACK_SIDE';
    idDocSetType?: string;
    country?: string;
  };
  fileName: string;
  mimeType: string;
}

export interface SolutionSumsubAdminApiSubmissionRequest {
  identityInfo?: Record<string, unknown>;
  questionnaireAnswers?: Record<string, unknown>;
  operationalState?: Record<string, unknown>;
  documents?: SolutionSumsubAdminApiDocumentInput[];
  documentPaths?: {
    frontPath?: string;
    backPath?: string;
    proofOfAddressPath?: string;
    incomeProofPath?: string;
    assetProofPath?: string;
    supportingDocumentPaths?: Array<{
      documentType: string;
      path: string;
    }>;
  };
  remark?: string;
  remarkChannel?: string;
  _yotpToken?: string;
  restrictedNationalityOverride?: RestrictedNationalityOverrideRequest;
}

export interface SolutionSumsubAdminApiFormValues {
  identityInfo?: Record<string, unknown>;
  questionnaireAnswers?: Record<string, unknown>;
  questionnaireFiles?: Record<string, unknown>;
  operationalState?: Record<string, unknown>;
  documentSets?: Array<Record<string, unknown>>;
}

export interface SolutionSumsubKycDisplayItem {
  key: string;
  label: string;
  description?: string | null;
  value?: unknown;
  valueType?: string;
  source?: string;
}

export interface SolutionSumsubKycDisplayGroup {
  key: string;
  title: string;
  description?: string | null;
  items: SolutionSumsubKycDisplayItem[];
}

export type SolutionSumsubKycRecoveryIssueSource = 'requestCheck' | 'documentUpload.errors' | 'documentUpload.warnings' | 'deactivatedImages';

export interface SolutionSumsubKycRecoveryIssue {
  source: SolutionSumsubKycRecoveryIssueSource;
  inspectionId?: string;
  imageId?: string;
  idDocType?: string;
  idDocSubType?: string;
  code?: string;
  label?: string;
  message?: string;
  description?: string;
  detectedAt?: string;
}

export interface SolutionSumsubKycRecoveryNote {
  type?: string;
  message: string;
  createdAt?: string;
}

export interface SolutionSumsubKycRecoveryContext {
  status: string;
  requestCheck?: {
    status?: string;
    reason?: string;
    error?: string;
    lastError?: string;
    detectedAt?: string;
  } | null;
  documentUpload?: {
    status?: string;
    reason?: string;
    detectedAt?: string;
    errors?: SolutionSumsubKycRecoveryIssue[];
    warnings?: SolutionSumsubKycRecoveryIssue[];
  } | null;
  deactivatedImages?: SolutionSumsubKycRecoveryIssue[];
  operatorNotes?: SolutionSumsubKycRecoveryNote[];
  blockingIssues: SolutionSumsubKycRecoveryIssue[];
  availableActions: Array<'UPDATE_SUMSUB_KYC' | 'REQUEST_CHECK'>;
}

export interface SolutionApplicationSumsubKycDetails {
  application: {
    id: string;
    status: string;
    solutionId: string;
    solutionCode?: string;
    solutionName?: string;
    userId: string;
    initialState?: Record<string, unknown>;
    cardProduct?: Record<string, unknown> | null;
    state?: Record<string, unknown>;
  };
  sumsubKyc: {
    enabled: boolean;
    status: SolutionSumsubKycStatus;
    levelName: string;
    profileKey?: string;
    submissionMode: 'SDK' | 'API';
    initiationSource: 'CLIENT_APP' | 'OPS_ADMIN';
    syncTarget: 'CLIENT_IDENTITY' | 'SOLUTION_APPLICATION';
    applicantId?: string | null;
    externalUserId?: string | null;
    importedApplicantLevelName?: string | null;
    importedSource?: string | null;
    levelNameMismatch?: boolean;
    reviewStatus?: string | null;
    reviewAnswer?: 'GREEN' | 'RED' | null;
    actionRequiredReason?: string | null;
    rejectionSummary?: string | null;
    rejectionLabels?: string[];
    reviewRejectType?: string | null;
    moderationComment?: string | null;
    clientComment?: string | null;
    requiredForSolutionApproval?: boolean;
    submittedAt?: string | null;
    approvedAt?: string | null;
    rejectedAt?: string | null;
    updatedAt?: string | null;
  };
  schema: SolutionSumsubAdminApiFormSchema;
  display: {
    applicantGroups: SolutionSumsubKycDisplayGroup[];
    questionnaireSections: SolutionSumsubKycDisplayGroup[];
    documentGroups: SolutionSumsubKycDisplayGroup[];
    reviewDetails: SolutionSumsubKycDisplayItem[];
  };
  updateInitialValues: SolutionSumsubAdminApiFormValues;
  recoveryContext: SolutionSumsubKycRecoveryContext | null;
  raw: Record<string, never>;
}

export interface SolutionApplicationSumsubLiveStatus {
  applicantFound: boolean;
  applicantId?: string | null;
  externalUserId?: string | null;
  levelName?: string | null;
  expectedLevelName: string;
  levelNameMismatch: boolean;
  reviewStatus?: string | null;
  reviewAnswer?: 'GREEN' | 'RED' | null;
  canSync: boolean;
  canTakeOver: boolean;
  blockedReason?: string | null;
}

export interface SolutionApplicationSumsubManualSyncResponse {
  workflowStarted: boolean;
  workflowId?: string;
  applicantId: string;
  reviewStatus: string;
  reviewAnswer: 'GREEN' | 'RED' | null;
  blockedReason?: string | null;
}

export interface SolutionSumsubAdminApiValidationError {
  field: string;
  message: string;
  code?: string;
}

export interface SolutionSumsubAdminApiFormValidationResult {
  canSubmit: boolean;
  applicationId?: string;
  levelName: string;
  documents: SolutionSumsubAdminApiDocumentInput[];
  documentPaths?: SolutionSumsubAdminApiSubmissionRequest['documentPaths'];
  identityFieldCount: number;
  questionnaireAnswerCount: number;
  questionnaireId: string | null;
  validationErrors: SolutionSumsubAdminApiValidationError[];
}

export interface SolutionSumsubAdminApiSubmissionResponse {
  submitted: boolean;
  applicationId: string;
  workflowId: string;
  levelName: string;
  status: SolutionSumsubKycStatus;
}

export interface SubmitOrCreateSolutionApplicationSumsubAdminApiSubmissionRequest extends SolutionSumsubAdminApiSubmissionRequest {
  applicationId?: string;
}

export interface SubmitOrCreateSolutionApplicationSumsubAdminApiSubmissionResponse
  extends SolutionSumsubAdminApiSubmissionResponse {
  createdApplication: boolean;
}

export interface UploadOperatorDocumentResponse {
  gcsPath?: string;
  url?: string;
  signedUrl?: string;
  fileName?: string;
  mimeType?: string;
  applicationId?: string;
}

export const getSolutionSumsubAdminApiFormSchema = (solutionId: string) =>
  request<SolutionSumsubAdminApiFormSchema>(`/solution/${solutionId}/sumsub/admin-api/form-schema`, {
    method: 'GET'
  });

export const getSolutionApplicationSumsubKycDetails = (userId: string, applicationId: string) =>
  request<SolutionApplicationSumsubKycDetails>(`/${userId}/solution/application/${applicationId}/sumsub/admin-api/details`, {
    method: 'GET'
  });

export const refreshSolutionApplicationSumsubLiveStatus = (userId: string, applicationId: string) =>
  request<SolutionApplicationSumsubLiveStatus>(`/${userId}/solution/application/${applicationId}/sumsub/admin-api/live-status`, {
    method: 'GET'
  });

export const syncSolutionApplicationSumsubKycFromSumsub = (
  userId: string,
  applicationId: string,
  data: { _yotpToken: string }
) =>
  request<SolutionApplicationSumsubManualSyncResponse>(`/${userId}/solution/application/${applicationId}/sumsub/admin-api/sync`, {
    method: 'POST',
    data
  });

export const submitOrCreateSolutionApplicationSumsubAdminApiSubmission = (
  userId: string,
  solutionId: string,
  data: SubmitOrCreateSolutionApplicationSumsubAdminApiSubmissionRequest
) =>
  request<SubmitOrCreateSolutionApplicationSumsubAdminApiSubmissionResponse>(
    `/${userId}/solution/${solutionId}/sumsub/admin-api/submit`,
    {
      method: 'POST',
      data
    }
  );

export const preflightSolutionApplicationSumsubAdminApiSubmission = (
  userId: string,
  solutionId: string,
  data: SubmitOrCreateSolutionApplicationSumsubAdminApiSubmissionRequest
) =>
  request<SolutionSumsubAdminApiFormValidationResult>(
    `/${userId}/solution/${solutionId}/sumsub/admin-api/preflight`,
    {
      method: 'POST',
      data
    }
  );

export const uploadOperatorDocument = (file: File) => {
  const formData = new FormData();
  formData.append('file', file);

  return request<UploadOperatorDocumentResponse>(
    `/documents/operator`,
    {
      method: 'POST',
      data: formData
    }
  );
};
